import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Typography,
  Paper,
  Divider
} from "@mui/material";

import ApiEndpoints from "../../network/ApiEndPoints";
import { get, postJsonData } from "../../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import Spinner from "../../component/loading-screen/Spinner";

const GenerateSalarySlip = ({ open, onClose, onSuccess }) => {

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [formData, setFormData] = useState({
    user_id: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    leave_days: "" // <-- new field for leave days
  });

  const [formErrors, setFormErrors] = useState({});
  const [preview, setPreview] = useState(null);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    if (open) {
      fetchEmployees();
    } else {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    updatePreview();
  }, [formData]);

  const fetchEmployees = () => {
    get(
      `${ApiEndpoints.GET_USERS_BIGG}?is_employee=1`,
      { export: 1 },
      setLoading,
      (res) => setEmployees(res.data || []),
      (error) => apiErrorToast(error)
    );
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      leave_days: ""
    });
    setFormErrors({});
    setPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.user_id) errors.user_id = "Employee required";
    if (!formData.month) errors.month = "Month required";
    if (!formData.year) errors.year = "Year required";
    if (formData.leave_days && isNaN(formData.leave_days)) errors.leave_days = "Invalid leave days";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updatePreview = () => {
    const employee = employees.find((emp) => emp.id === formData.user_id);
    if (!employee) {
      setPreview(null);
      return;
    }
    const monthName = months.find((m) => m.value === formData.month)?.label;
    setPreview({
      name: employee.name,
      emp_id: employee.emp_id || employee.id,
      month: monthName,
      year: formData.year,
      leave_days: formData.leave_days
    });
  };

  const handleGenerate = () => {
    if (!validateForm()) return;
    postJsonData(
      ApiEndpoints.GENERATE_SALARY_SLIP,
      formData,
      setLoading,
      (res) => {
        okSuccessToast(res?.data?.message || "Salary slip generated successfully");
        if (onSuccess) onSuccess(res?.data?.data);
        resetForm();
        onClose();
      },
      (error) => apiErrorToast(error?.message || "Failed to generate salary slip")
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Spinner loading={loading} />
      <DialogTitle>Generate Salary Slip</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!formErrors.user_id}>
                <InputLabel>Employee *</InputLabel>
                <Select
                  name="user_id"
                  value={formData.user_id}
                  onChange={handleInputChange}
                  label="Employee *"
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.emp_id || "N/A"})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.user_id}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small" error={!!formErrors.month}>
                <InputLabel>Month *</InputLabel>
                <Select
                  name="month"
                  value={formData.month}
                  onChange={handleInputChange}
                  label="Month *"
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.month}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small" error={!!formErrors.year}>
                <InputLabel>Year *</InputLabel>
                <Select
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  label="Year *"
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.year}</FormHelperText>
              </FormControl>
            </Grid>

            {/* NEW: Leave Days Input */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Leave Days (optional)"
                type="number"
                name="leave_days"
                value={formData.leave_days}
                onChange={handleInputChange}
                error={!!formErrors.leave_days}
                helperText={formErrors.leave_days || "Enter number of unpaid leave days"}
              />
            </Grid>

            {preview && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                  <Typography variant="subtitle2">Preview</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">Employee : {preview.name}</Typography>
                  <Typography variant="body2">Period : {preview.month} {preview.year}</Typography>
                  {preview.leave_days && <Typography variant="body2">Leave Days : {preview.leave_days}</Typography>}
                </Paper>
              </Grid>
            )}

          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={!formData.user_id || loading}
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateSalarySlip;